import { notification, Tooltip } from "antd";
import React, { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { CheckCircleFilled } from "@ant-design/icons";
import { FiPrinter, FiEdit, FiClock } from "react-icons/fi";
import { FaRegCopy } from "react-icons/fa";
import api from "../../../../service";
import QuickEditReprint from "../QuickEditReprint";
import CredentialEditHistoryModal from "../CredentialEditHistoryModal";
import {
  ModalProtocol,
  HeaderContainer,
  TitleContainer,
  ItemsContainer,
  Items,
  Title,
  Infos,
  Actions,
  ButtonProtocol,
  Container,
  IconButton,
  Message,
} from "./styles";
import printJS from "print-js";
import { verifyDefaulter } from "../../../../redux/modules/defaulters/actions";
import DuplicateModal from "../DuplicateModal";
import PrintCredential from "../PrintCredential";

const PrintProtocol = ({
  data,
  visible,
  setVisible,
  fetchData,
  selectedRowKeys,
  setSelectedRows,
  companyNameCredential,
  credentialTypes,
  sortedData = [],
}) => {
  const intl = useIntl();
  const { token } = useSelector((state) => state.auth);
  const [fileData, setFileData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const { isDefaulter, isLoading } = useSelector((state) => state.defaulters);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const contractNumber = localStorage.getItem("serviceContractNumber");
  const dispatch = useDispatch();
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [availableCredentials, setAvailableCredentials] = useState(0);
  const [credentialTypeList, setCredentialTypeList] = useState(credentialTypes);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedCredential, setSelectedCredential] = useState(null);

  const singleSelection = selectedRowKeys.length === 1;

  useEffect(() => {
    if (visible) {
      handleProtocol();
    }
  }, [visible]);

  useEffect(() => {
    if (contractNumber) {
      dispatch(verifyDefaulter({ contractNumber }));
    }
  }, [contractNumber, dispatch]);

  // Get full credential (with collaboratorId) from the tab list,
  // since the selection only stores { id, collaboratorName, credentialType }.
  const getFullCredential = useCallback(
    (rowId) => sortedData.find((item) => item.id === rowId) || null,
    [sortedData]
  );

  const handleOpenEdit = useCallback(() => {
    if (!singleSelection) return;
    const selectedRow = selectedRowKeys[0];
    const fullRecord = getFullCredential(selectedRow.id);
    setSelectedCredential({
      id: selectedRow.id,
      collaboratorName: selectedRow.collaboratorName,
      collaboratorId: fullRecord?.collaboratorId,
    });
    setShowEditModal(true);
  }, [singleSelection, selectedRowKeys, getFullCredential]);

  const handleOpenHistory = useCallback(() => {
    if (!singleSelection) return;
    const selectedRow = selectedRowKeys[0];
    setSelectedCredential({
      id: selectedRow.id,
      collaboratorName: selectedRow.collaboratorName,
    });
    setShowHistoryModal(true);
  }, [singleSelection, selectedRowKeys]);

  const handleProtocol = useCallback(async () => {
    setLoading(true);

    try {
      const response = await api.get(`/credential-reports/${data?.protocol}`, {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const reportOutput = response.data?.reportOutput || [];

      setFileData(reportOutput);
    } catch (error) {
      notification.error({
        message: intl.formatMessage({ id: "error" }),
        description: intl.formatMessage({
          id: "api.error." + (error.response?.data?.code || "unknown"),
        }),
      });
    } finally {
      setLoading(false);
    }
  }, [data]);

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");

    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  const handleShowDuplicateModal = () => {
    const selectedCredentialType = selectedRowKeys[0]?.credentialType;
    setAvailableCredentials(credentialTypeList.find(
      (type) => type.credentialType === selectedCredentialType
    )?.associateAvailable || 0);
    setShowDuplicateModal(true);
  };

  const decreaseCredentialTypesAvailableByType = (duplicateType) => {
    setCredentialTypeList((prevList) =>
      prevList.map((type) =>
        type.credentialType === duplicateType
          ? { ...type, associateAvailable: type.associateAvailable - 1 }
          : type
      )
    );
  };

  return (
    <>
      {!isLoading && (
        <PrintCredential
          defaulter={isDefaulter}
          fetchData={fetchData}
          open={showPrintModal}
          setOpen={setShowPrintModal}
          selectedRowKeys={selectedRowKeys}
          setSelectedRows={setSelectedRows}
          companyNameCredential={companyNameCredential}
          isFirstPrint={false}
        />
      )}

      {!isLoading && (
        <DuplicateModal
          defaulter={isDefaulter}
          fetchData={fetchData}
          open={showDuplicateModal}
          setOpen={setShowDuplicateModal}
          selectedRowKeys={selectedRowKeys}
          setSelectedRows={setSelectedRows}
          availableCredentials={availableCredentials}
          updateAvailableCredentials={decreaseCredentialTypesAvailableByType}
        />
      )}

      {!isLoading && (
        <QuickEditReprint
          open={showEditModal}
          setOpen={setShowEditModal}
          selectedCredential={selectedCredential}
          fetchData={fetchData}
        />
      )}

      {!isLoading && (
        <CredentialEditHistoryModal
          open={showHistoryModal}
          setOpen={setShowHistoryModal}
          credential={selectedCredential}
        />
      )}

      <ModalProtocol
        closable={false}
        width={580}
        open={visible}
        footer={
          <div
            style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}
          >
            <ButtonProtocol
              loading={loading}
              onClick={() => {
                if (fileData) {
                  printJS({
                    printable: fileData.file,
                    type: "pdf",
                    base64: true,
                  });
                }

                setVisible(false);
              }}
              style={{ border: "none", width: "183px" }}
            >
              <FiPrinter /> {intl.formatMessage({ id: "print_protocol" })}
            </ButtonProtocol>
            <ButtonProtocol
              type="primary"
              onClick={() => {
                setVisible(false);
                setFileData(null);
              }}
              style={{
                backgroundColor: "rgba(6, 142, 195, 1)",
                width: "87px",
              }}
            >
              {intl.formatMessage({ id: "close" })}
            </ButtonProtocol>
          </div>
        }
      >
        <HeaderContainer>
          <CheckCircleFilled
            style={{ fontSize: "27px", color: "rgba(6, 142, 195, 1)" }}
          />
          <TitleContainer>
            <Title>{intl.formatMessage({ id: "withdrawn_protocol" })}</Title>
            <Infos>
              Nº {String(data?.protocol || "").padStart(4, "0")} •{" "}
              {formatDateTime(data?.withdrawnAt)}
            </Infos>
          </TitleContainer>
        </HeaderContainer>
        <ItemsContainer>
          <Items>
            {" "}
            <span>{intl.formatMessage({ id: "withdrawn_by" })}</span>
            <strong>{data?.nameOfSignature}</strong>
          </Items>
          <Items>
            <span>{intl.formatMessage({ id: "email" })}</span>
            <strong>{data?.emailOfSignature}</strong>
          </Items>
          <Items>
            <span>{intl.formatMessage({ id: "telephone" })}</span>
            <strong>{data?.phoneOfSignature}</strong>
          </Items>
        </ItemsContainer>
      </ModalProtocol>
      <Container>
        <Message>
          {selectedRowKeys.length > 0 ? (
            <>
              <span>
                {intl.formatMessage({
                  id: "selected_credentials",
                })}
              </span>
              <span style={{ fontWeight: "bold", marginLeft: "5px" }}>
                {String(selectedRowKeys.length).padStart(2, "0")}
              </span>
            </>
          ) : (
            intl.formatMessage({
              id: "select_at_least_one_credential_to_reprint_or_generate_duplicate",
            })
          )}
        </Message>
        <Actions>
          <Tooltip
            title={intl.formatMessage({
              id: "edit_and_reprint_credential",
            })}
            placement="top"
          >
            <span style={{ display: "inline-flex" }}>
              <IconButton
                onClick={handleOpenEdit}
                disabled={!singleSelection}
                active={singleSelection}
              >
                <FiEdit />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip
            title={intl.formatMessage({ id: "edit_reprint_history_title" })}
            placement="top"
          >
            <span style={{ display: "inline-flex" }}>
              <IconButton
                onClick={handleOpenHistory}
                disabled={!singleSelection}
                active={singleSelection}
              >
                <FiClock />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip
            title={intl.formatMessage({ id: "reprint_credential" })}
            placement="top"
          >
            <span style={{ display: "inline-flex" }}>
              <IconButton
                onClick={() => setShowPrintModal(true)}
                disabled={selectedRowKeys.length === 0}
                active={selectedRowKeys.length > 0}
              >
                <FiPrinter />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip
            title={intl.formatMessage({ id: "second_copy" })}
            placement="top"
          >
            <span style={{ display: "inline-flex" }}>
              <IconButton
                onClick={handleShowDuplicateModal}
                disabled={selectedRowKeys.length !== 1}
                active={selectedRowKeys.length === 1}
              >
                <FaRegCopy />
              </IconButton>
            </span>
          </Tooltip>
        </Actions>
      </Container>
    </>
  );
};

export default PrintProtocol;
